import React, { useEffect, useMemo } from 'react';
import {
  Alert, Box, Dialog, IconButton, InputAdornment, OutlinedInput, Select, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocalText from '../common/LocalText';
import { useLocalText } from '../common/useLocalText';
import UsersTable from './UsersTable';
import Pagination from './Pagination';
import ActivityPeriodFilter from './ActivityPeriodFilter';
import AnalyticsErrorStrip from './AnalyticsErrorStrip';
import { useUsersList } from '../../hooks/useUsersList';
import {
  ACTIVITY_DEFAULT_FROM, CARDS, CARDS_WITH_CMS_ONLY_USERS, CMS_NO_LOGIN_HINT, PAGE_SIZES, SEARCH_MAX_LENGTH,
  analyticsUiTokens as T,
} from '../../constants/userAnalytics';
import { fmtCount } from '../../utils/analyticsMetrics';
import { todayEastern } from '../../utils/dateUtils';

const TITLE_ID = 'ua-users-modal-title';

// Thin scrollbars (both directions) in the same colours the other scrollable CMS panels use.
const thinScrollbar = {
  scrollbarWidth: 'thin',
  scrollbarColor: '#c7d8ec transparent',
  '&::-webkit-scrollbar': { width: 6, height: 6 },
  '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
  '&::-webkit-scrollbar-corner': { backgroundColor: 'transparent' },
  '&::-webkit-scrollbar-thumb': { backgroundColor: '#c7d8ec', borderRadius: 4 },
  '&::-webkit-scrollbar-thumb:hover': { backgroundColor: T.slate[400] },
};

const DrilldownBody = ({ card, onClose, requestFilters, resetKey, cardTotal, subtitle, cmsTracked }) => {
  const list = useUsersList({ card, filters: requestFilters, resetKey });
  const { data, status, error, page, pageSize, searchInput, searching } = list;
  const meta = CARDS[card] ?? CARDS.total;
  const HeaderIcon = meta.Icon;

  const searchPlaceholder = useLocalText('Search by user name or email address...');
  const clearSearchLabel = useLocalText('Clear search');
  const rowsLabel = useLocalText('Rows per page');
  const closeLabel = useLocalText('Close');

  const defaultRange = useMemo(() => ({ from: ACTIVITY_DEFAULT_FROM, to: todayEastern() }), []);
  const activityRange = list.activity ?? data?.activity ?? defaultRange;

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const firstLoad = !data && (status === 'loading' || status === 'idle');
  const refetching = status === 'refreshing';
  const pillCount = cardTotal ?? (!searching && data ? data.total : null);
  const showCmsHint = CARDS_WITH_CMS_ONLY_USERS.includes(card) && !cmsTracked;

  // The pill (the card's number) and the unsearched list total must agree; if they ever do not,
  // the shared population query has drifted. Developer aid only.
  useEffect(() => {
    if (process.env.NODE_ENV === 'production' || status !== 'ready' || searching || cardTotal == null || !data) return;
    if (data.total !== cardTotal) {
      // eslint-disable-next-line no-console
      console.warn(`[UserAnalytics] "${card}" list total (${data.total}) differs from the card value (${cardTotal}).`);
    }
  }, [status, searching, cardTotal, data, card]);

  // Numbered from the page the rows on screen actually belong to: while the next page loads the old rows
  // stay visible (dimmed), and must not be relabelled with the new page's numbers.
  const shownPage = data?.page ?? page;
  const shownSize = data?.pageSize ?? pageSize;
  const from = total === 0 ? 0 : (shownPage - 1) * shownSize + 1;
  const to = Math.min(shownPage * shownSize, total);

  return (
    <>
      <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.75, sm: 2 }, bgcolor: T.navy, color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: '12px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)', color: T.sky[300] }}>
            <HeaderIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
              <Typography id={TITLE_ID} component="h2" sx={{ fontSize: { xs: 16, sm: 18 }, fontWeight: 700, letterSpacing: '-0.01em', minWidth: 0 }} noWrap>
                <LocalText>{meta.modalTitle}</LocalText>
              </Typography>
              {pillCount != null && (
                <Box
                  component="span"
                  data-testid="modal-count-pill"
                  sx={{ px: 1.25, py: 0.25, fontSize: 12, fontWeight: 800, borderRadius: '9999px', bgcolor: 'rgba(56,189,248,0.2)', color: T.sky[200], border: '1px solid rgba(125,211,252,0.3)' }}
                >
                  {fmtCount(pillCount)} <LocalText>{pillCount === 1 ? 'User' : 'Users'}</LocalText>
                </Box>
              )}
            </Box>
            <Typography data-testid="modal-subtext" noWrap sx={{ fontSize: 11, color: T.slate[300], mt: 0.25 }}>{subtitle}</Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} aria-label={closeLabel} sx={{ width: 32, height: 32, flexShrink: 0, bgcolor: 'rgba(255,255,255,0.1)', color: T.slate[200], '&:hover': { bgcolor: 'rgba(255,255,255,0.2)', color: '#ffffff' } }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 3 }, py: 1.5, bgcolor: T.slate[50], borderBottom: `1px solid ${T.cardBorder}`, display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 2, rowGap: 1.25, flexShrink: 0 }}>
          <OutlinedInput
            size="small"
            value={searchInput}
            onChange={(e) => list.setSearchInput(e.target.value)}
            placeholder={searchPlaceholder}
            inputProps={{ 'aria-label': searchPlaceholder, maxLength: SEARCH_MAX_LENGTH, autoComplete: 'off' }}
            startAdornment={<InputAdornment position="start"><SearchOutlinedIcon sx={{ fontSize: 18, color: T.slate[400] }} /></InputAdornment>}
            endAdornment={searchInput ? (
              <InputAdornment position="end">
                <IconButton size="small" edge="end" aria-label={clearSearchLabel} onClick={() => list.setSearchInput('')} sx={{ color: T.slate[400] }}>
                  <CancelIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : null}
            sx={{ flex: '1 1 240px', maxWidth: { sm: 340 }, bgcolor: '#ffffff', borderRadius: '8px', fontSize: 12, '& fieldset': { borderColor: T.slate[200] }, '&.Mui-focused fieldset': { borderColor: `${T.navy} !important`, borderWidth: '1px !important' } }}
          />
          <ActivityPeriodFilter
            applied={list.activity}
            defaultRange={defaultRange}
            onApply={list.applyActivity}
            onReset={list.resetActivity}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: { sm: 'auto' } }}>
            <Box component="label" htmlFor="ua-rows-per-page" sx={{ fontSize: 11, fontWeight: 600, color: T.slate[500] }}>
              <LocalText>Rows per page:</LocalText>
            </Box>
            <Select
              native
              size="small"
              value={pageSize}
              onChange={(e) => list.changePageSize(Number(e.target.value))}
              inputProps={{ id: 'ua-rows-per-page', 'aria-label': rowsLabel }}
              sx={{ bgcolor: '#ffffff', borderRadius: '8px', fontSize: 12, fontWeight: 600, color: T.navy, '& .MuiSelect-select': { py: '5px' }, '& fieldset': { borderColor: T.slate[200] } }}
            >
              {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
            </Select>
          </Box>
      </Box>

      {showCmsHint && (
        <Alert icon={<InfoOutlinedIcon fontSize="small" />} severity="info" role="note" data-testid="cms-hint" sx={{ borderRadius: 0, py: 0.25, fontSize: 12, flexShrink: 0 }}>
          <LocalText>{CMS_NO_LOGIN_HINT}</LocalText>
        </Alert>
      )}

      {error && data && (
        <Box sx={{ px: { xs: 2, sm: 3 }, py: 1, flexShrink: 0 }}>
          <AnalyticsErrorStrip error={error} hasData what="users" onRetry={list.refetch} />
        </Box>
      )}

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', bgcolor: '#ffffff', ...thinScrollbar }}>
        {error && !data ? (
          <Box sx={{ p: 3 }}>
            <AnalyticsErrorStrip error={error} hasData={false} what="users" onRetry={list.refetch} />
          </Box>
        ) : data && items.length === 0 ? (
          <Box data-testid="users-empty" sx={{ py: 6, px: 2, textAlign: 'center' }}>
            <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: T.slate[100], color: T.slate[400], display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1.5 }}>
              <SearchOutlinedIcon sx={{ fontSize: 26 }} />
            </Box>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.slate[700] }}>
              <LocalText>{searching ? 'No matching users found' : 'No users in this group for the selected filters'}</LocalText>
            </Typography>
            <Typography sx={{ fontSize: 12, color: T.slate[500], mt: 0.5 }}>
              <LocalText>{searching ? 'Try adjusting your search query or clearing the search box.' : 'Try a different date period or filter.'}</LocalText>
            </Typography>
          </Box>
        ) : (
          <UsersTable items={items} offset={(shownPage - 1) * shownSize} activity={activityRange} loading={firstLoad} dimmed={refetching} />
        )}
      </Box>

      <Box sx={{ px: { xs: 2, sm: 3 }, py: 1.5, bgcolor: T.slate[50], borderTop: `1px solid ${T.cardBorder}`, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { sm: 'center' }, justifyContent: 'space-between', gap: 1.5, flexShrink: 0 }}>
        <Typography data-testid="modal-summary" sx={{ fontSize: 12, fontWeight: 500, color: T.slate[500], textAlign: { xs: 'center', sm: 'left' } }}>
          <LocalText>Showing</LocalText>{' '}
          <Box component="strong" sx={{ color: T.slate[800] }}>{total === 0 ? '0' : `${fmtCount(from)}–${fmtCount(to)}`}</Box>{' '}
          <LocalText>of</LocalText>{' '}
          <Box component="strong" sx={{ color: T.slate[800] }}>{fmtCount(total)}</Box>{' '}
          <LocalText>users</LocalText>
        </Typography>
        {total > 0 && <Pagination page={page} totalPages={totalPages} onChange={list.goToPage} />}
      </Box>
    </>
  );
};

/**
 * Drill-down popup behind the six value cards. The shell is a MUI Dialog (focus trap, Escape,
 * backdrop click, focus restore to the card for free); below `sm` it is a full-screen sheet.
 *
 * Its content mounts fresh on every open, which is what resets search, page, page size and the
 * activity period. It reads the page's filters (read-only) and never refetches the summary.
 */
const UsersDrilldownModal = ({ open, card, onClose, requestFilters, resetKey, cardTotal, subtitle, cmsTracked }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={open && Boolean(card)}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth={false}
      aria-labelledby={TITLE_ID}
      slotProps={{
        backdrop: { sx: { bgcolor: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(2px)' } },
        paper: {
          'data-testid': 'users-modal',
          sx: { width: '100%', maxWidth: 1320, height: fullScreen ? '100%' : 'min(90vh, 800px)', maxHeight: fullScreen ? '100%' : '90vh', m: { xs: 0, sm: 2 }, borderRadius: fullScreen ? 0 : '16px', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
        },
      }}
    >
      <DrilldownBody
        card={card}
        onClose={onClose}
        requestFilters={requestFilters}
        resetKey={resetKey}
        cardTotal={cardTotal}
        subtitle={subtitle}
        cmsTracked={cmsTracked}
      />
    </Dialog>
  );
};

export default UsersDrilldownModal;
